import { useCallback, useEffect, useMemo, useState } from 'react'

import { getKpiGoals } from '@/api/kpi'
import { formatMetric } from '@/hooks/clarity/util'
import { KpiGoalsModel } from '@/types/clarity'

export default function useKpiGoals(
  startDate: string,
  endDate: string,
  branches: string,
  tenant: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [kpiGoals, setKpiGoals] = useState<KpiGoalsModel>()

  const fetchData = useMemo(() => {
    return async () => {
      try {
        let kpiGoals: KpiGoalsModel = {}

        if (tenant !== 'blankstreet') {
          kpiGoals = (await fetchKpiGoals(branches, startDate, endDate))
            .kpiGoals
        }

        setKpiGoals(kpiGoals)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData])

  const refetch = useCallback(() => {
    console.log('Refetching KPI goals data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    kpiGoals,
    loading,
    error,
    refetch,
  }
}

async function fetchKpiGoals(
  branches: string,
  startDate: string,
  endDate: string
): Promise<{
  kpiGoals: KpiGoalsModel
}> {
  try {
    const response = await getKpiGoals(branches, startDate, endDate)

    const goals: KpiGoalsModel = setKpiGoals(
      response.net_sales,
      response.google_reviews,
      response.speed_of_service,
      response.total_traffic,
      response.cogs_pti,
      response.labor_ratio
    )

    return {
      kpiGoals: goals,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function setKpiGoals(
  netSales?: number,
  googleReviews?: number,
  speedOfService?: number,
  totalTraffic?: number,
  cogs?: number,
  laborRatio?: number
): KpiGoalsModel {
  return {
    netSales:
      netSales !== undefined && netSales !== null
        ? formatMetric(netSales, 2)
        : undefined,
    googleReviews:
      googleReviews !== undefined && googleReviews !== null
        ? formatMetric(googleReviews, 2)
        : undefined,
    speedOfService:
      speedOfService !== undefined && speedOfService !== null
        ? formatMetric(speedOfService, 2)
        : undefined,
    totalTraffic:
      totalTraffic !== undefined && totalTraffic !== null
        ? formatMetric(totalTraffic, 2)
        : undefined,
    cogs:
      cogs !== undefined && cogs !== null ? formatMetric(cogs, 2) : undefined,
    laborRatio:
      laborRatio !== undefined && laborRatio !== null
        ? formatMetric(laborRatio, 2)
        : undefined,
  }
}
